/**
 * @file tags.service.js
 * @description Pure helpers to assign and display the tags stored in records (products, vehicles, suppliers).
 */

/**
 * Build the tags to store in a record from the tag ids selected in a tags select.
 * Name and slug are taken from the tags catalog.
 * @param {string[]} selectedTagIds - The selected tag ids.
 * @param {Object[]} tagsOptions - The tags catalog ({ _id, name, slug }).
 * @returns {Object[]} The record tags ({ ref, name, slug }).
 */
export function buildTagsFromSelection(selectedTagIds = [], tagsOptions = []) {
  return tagsOptions
    .filter(tag => selectedTagIds.includes(tag._id))
    .map(tag => {
      return { ref: tag._id, name: tag.name, slug: tag.slug };
    });
}

/**
 * Copy the tags stored in a record, keeping only the fields the API accepts.
 * @param {Object[]} storedTags - The stored record tags.
 * @returns {Object[]} The record tags ({ ref, name, slug }).
 */
export function copyTags(storedTags = []) {
  return storedTags.map(tag => {
    return { ref: tag.ref, name: tag.name, slug: tag.slug };
  });
}

/**
 * Get the tag ids of the record tags, used as the value of a tags select.
 * @param {Object[]} tags - The record tags ({ ref, name, slug }).
 * @returns {string[]} The tag ids.
 */
export function getTagIds(tags = []) {
  return tags.map(tag => tag.ref);
}

/**
 * Join the names of the record tags to display them in a single line.
 * @param {Object[]} tags - The record tags ({ ref, name, slug }).
 * @returns {string} The tag names separated by " | ", or "-" when there are none.
 */
export function joinTagNames(tags = []) {
  return tags.map(tag => tag.name).join(" | ") || "-";
}
